import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Zap, Star, Crown, Gift, CreditCard, Lock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLANS = [
  {
    id: 'free',
    name: 'Siempre Gratis',
    price_monthly: 0,
    price_annual: 0,
    color: 'from-gray-700 to-gray-800',
    accent: 'text-gray-300',
    badge: null,
    icon: Gift,
    stripe_tier: 'free',
    description: 'Para explorar OkBot sin costo',
    features: [
      { text: 'Chat con modelos gratuitos', included: true },
      { text: 'GPT-oss, Gemma 3, Nemotron', included: true },
      { text: 'Todos los módulos básicos', included: true },
      { text: 'Calendario, Notas, Listas', included: true },
      { text: '50 mensajes/día', included: true },
      { text: 'Soporte por email', included: true },
      { text: 'Modelos de pago (GPT-4o Mini+)', included: false },
      { text: 'Sin límite diario', included: false },
      { text: 'Agentes IA personalizados', included: false },
      { text: 'API access', included: false },
    ]
  },
  {
    id: 'essential',
    name: 'Esencial',
    price_monthly: 149,
    price_annual: 1490,
    color: 'from-blue-700 to-blue-900',
    accent: 'text-blue-300',
    badge: null,
    icon: Zap,
    stripe_tier: 'essential',
    description: 'Modelos básicos de pago para uso personal',
    features: [
      { text: 'Todo del plan Gratis', included: true },
      { text: 'GPT-4o Mini', included: true },
      { text: 'Claude 3 Haiku', included: true },
      { text: 'Gemini Flash 1.5', included: true },
      { text: '500 mensajes/día', included: true },
      { text: 'Sin anuncios', included: true },
      { text: 'Soporte prioritario', included: true },
      { text: 'Modelos avanzados (GPT-4o+)', included: false },
      { text: 'Mensajes ilimitados', included: false },
      { text: 'API access', included: false },
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price_monthly: 349,
    price_annual: 3490,
    color: 'from-red-700 to-red-900',
    accent: 'text-red-300',
    badge: '⭐ Más popular',
    icon: Star,
    stripe_tier: 'pro',
    description: 'Modelos avanzados y todas las funcionalidades',
    features: [
      { text: 'Todo del plan Esencial', included: true },
      { text: 'GPT-4o', included: true },
      { text: 'Claude 3.5 Sonnet', included: true },
      { text: 'Gemini Pro 1.5', included: true },
      { text: 'DeepSeek R1', included: true },
      { text: 'Mensajes ilimitados', included: true },
      { text: 'Agentes IA personalizados', included: true },
      { text: 'API access', included: true },
      { text: 'Widgets ilimitados', included: true },
      { text: 'Claude 3 Opus / o3-mini', included: false },
    ]
  },
  {
    id: 'elite',
    name: 'Elite',
    price_monthly: 799,
    price_annual: 7990,
    color: 'from-purple-700 to-purple-900',
    accent: 'text-purple-300',
    badge: '👑 Máximo poder',
    icon: Crown,
    stripe_tier: 'elite',
    description: 'Todos los modelos incluyendo los más potentes',
    features: [
      { text: 'Todo del plan Pro', included: true },
      { text: 'Claude 3 Opus', included: true },
      { text: 'o3-mini (OpenAI)', included: true },
      { text: 'Llama 3.1 405B', included: true },
      { text: 'Business Dashboard', included: true },
      { text: 'Multi-usuario (hasta 5)', included: true },
      { text: 'Soporte VIP 24/7', included: true },
      { text: 'Integraciones enterprise', included: true },
      { text: 'SLA garantizado', included: true },
      { text: 'Onboarding personalizado', included: true },
    ]
  }
];

function PlanCard({ plan, currentTier, billingCycle, onSelect, checkoutLoading }) {
  const Icon = plan.icon;
  const isCurrentPlan = currentTier === plan.id;
  const isLoading = checkoutLoading === plan.id;
  const price = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
  const perMonth = billingCycle === 'annual' && plan.price_monthly > 0 ? Math.round(plan.price_annual / 12) : plan.price_monthly;
  const savings = billingCycle === 'annual' && plan.price_monthly > 0 ? Math.round((plan.price_monthly * 12 - plan.price_annual) / (plan.price_monthly * 12) * 100) : 0;

  return (
    <div className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-300 ${isCurrentPlan ? 'border-white/40 shadow-2xl scale-[1.01]' : 'border-white/10 hover:border-white/25 hover:scale-[1.01]'}`}>
      {plan.badge && (
        <div className="absolute top-4 right-4 bg-white/10 backdrop-blur text-xs font-medium px-2 py-1 rounded-full text-white">
          {plan.badge}
        </div>
      )}
      {isCurrentPlan && (
        <div className="absolute top-4 left-4 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
          Tu plan actual
        </div>
      )}
      <div className={`bg-gradient-to-br ${plan.color} p-6`}>
        <div className={`w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-3`}>
          <Icon size={22} className="text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
        <p className="text-white/70 text-sm mt-0.5">{plan.description}</p>
        <div className="mt-4">
          {plan.price_monthly === 0 ? (
            <div className="text-4xl font-black text-white">GRATIS</div>
          ) : (
            <>
              {billingCycle === 'annual' ? (
                <>
                  <div className="text-4xl font-black text-white">
                    ${price.toLocaleString('es-MX')} <span className="text-lg font-normal text-white/70">MXN/año</span>
                  </div>
                  <div className="text-white/60 text-sm mt-0.5">${perMonth.toLocaleString('es-MX')} MXN/mes · {savings}% de ahorro</div>
                </>
              ) : (
                <div className="text-4xl font-black text-white">
                  ${price.toLocaleString('es-MX')} <span className="text-lg font-normal text-white/70">MXN/mes</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className="bg-black/60 p-5 space-y-2.5">
        {plan.features.map((f, i) => (
          <div key={i} className={`flex items-start gap-2.5 text-sm ${f.included ? 'text-gray-200' : 'text-gray-600'}`}>
            {f.included
              ? <Check size={15} className="text-green-400 flex-shrink-0 mt-0.5" />
              : <X size={15} className="text-gray-700 flex-shrink-0 mt-0.5" />}
            <span className={f.included ? '' : 'line-through'}>{f.text}</span>
          </div>
        ))}
        <div className="pt-3">
          {isCurrentPlan ? (
            <div className="w-full py-2.5 rounded-xl text-center text-sm font-medium bg-white/10 text-white/60 cursor-default">
              Plan Activo
            </div>
          ) : plan.price_monthly === 0 ? (
            <button className="w-full py-2.5 rounded-xl text-center text-sm font-medium bg-white/10 text-white/60 cursor-default">
              Plan Gratuito
            </button>
          ) : (
            <button onClick={() => onSelect(plan)} disabled={!!checkoutLoading}
              className={`w-full py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r ${plan.color} text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60`}>
              {isLoading
                ? <><Loader2 size={15} className="animate-spin" /> Redirigiendo...</>
                : <><CreditCard size={15} /> Suscribirse</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PlansModule({ currentUser, onUpgrade }) {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const currentTier = currentUser?.stripe_tier || 'free';

  const handleSelect = async (plan) => {
    if (onUpgrade) {
      onUpgrade(plan);
      return;
    }
    if (plan.price_monthly === 0) return;

    setCheckoutLoading(plan.id);
    try {
      const res = await axios.post(`${API}/payments/okbot-plans/checkout`, {
        tier: plan.stripe_tier,
        billing: billingCycle,
        user_id: currentUser?.id || null,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('No se pudo generar el enlace de pago.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al procesar el pago. Intenta de nuevo.';
      toast.error(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white">Planes OkBot</h2>
        <p className="text-gray-400 mt-1 text-sm">Elige el plan que mejor se adapte a tus necesidades</p>
      </div>

      <div className="flex items-center justify-center">
        <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1">
          <button onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            Mensual
          </button>
          <button onClick={() => setBillingCycle('annual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${billingCycle === 'annual' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            Anual <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">2 meses gratis</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map(plan => (
          <PlanCard key={plan.id} plan={plan} currentTier={currentTier} billingCycle={billingCycle} onSelect={handleSelect} checkoutLoading={checkoutLoading} />
        ))}
      </div>

      <div className="text-center space-y-1 pt-2">
        <p className="text-gray-600 text-xs flex items-center justify-center gap-1.5">
          <Lock size={10} /> Pagos seguros con Stripe · Cancela en cualquier momento · Precios en MXN (IVA incluido)
        </p>
      </div>
    </div>
  );
}
